/**
 * Decision Lens — global app-wide preference.
 *
 * The lens (Family / Bachelor / Investor) is a user-level context, not a
 * card-level one. Selecting Family in one card should make *every* open or
 * future card behave as Family until the user changes it again.
 *
 * This module is the single source of truth:
 *   - module-level `current` state, persisted to localStorage
 *   - `subscribe(listener)` fan-out so any rendered UI that depends on the
 *     mode (verdict card, compare card, the global pill control) refreshes
 *     itself the instant the mode flips
 *   - `renderGlobalLensControl(parent)` installs the one top-level
 *     segmented pill control that the whole app uses
 *
 * No backend, no schema, no API — everything is in-memory + localStorage.
 */

import { setFullMapView } from "./full-map-view";
import { type CityCode } from "./budget-format";
import { setPriority } from "./constraint-priority";
import { setBudgetBucket, setUserBudgetRange } from "./budget-band";
import { setPurchaseMode } from "./purchase-mode";
import { setPropertyType } from "./property-type";

export type LensMode = "none" | "family" | "bachelor" | "investor";

/**
 * Subset of LensMode that excludes the Exploration default. Use this on
 * any internal record/switch that only has meaning for a persona —
 * avoids forcing dummy "none" entries into lens-specific tables.
 */
export type PersonaMode = Exclude<LensMode, "none">;

const STORAGE_KEY = "hyperlocal-lens-mode";

/**
 * Session/state value. App boots in Exploration mode (`"none"`) so new
 * users see a clean map before any decision filters apply.
 */
let current: LensMode = loadInitial();

/** Subscribers refreshed on every mode change. */
const listeners = new Set<(m: LensMode) => void>();

function loadInitial(): LensMode {
  try {
    if (typeof localStorage !== "undefined") {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v === "none" || v === "family" || v === "bachelor" || v === "investor") {
        return v;
      }
    }
  } catch {
    /* storage blocked (safari private mode, etc.) — fall through */
  }
  return "none";
}

export function getMode(): LensMode {
  return current;
}

/** Human label for the mode — used by card badges so copy stays consistent. */
export function getModeLabel(m: LensMode = current): string {
  if (m === "none") return "Any";
  return m === "family" ? "Family" : m === "bachelor" ? "Bachelor" : "Investor";
}

export function setMode(m: LensMode): void {
  if (m === current) return;
  current = m;
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_KEY, m);
    }
  } catch {
    /* ignore */
  }
  // Fan out to every subscribed renderer.
  for (const fn of Array.from(listeners)) {
    try {
      fn(m);
    } catch {
      /* a broken subscriber must not break the rest */
    }
  }
}

/**
 * Subscribe a listener. Returns an unsubscribe function. Cards should call
 * this on open and call the returned unsubscribe on close, so they refresh
 * whenever the global mode flips but don't leak handlers after dismissal.
 */
export function subscribe(fn: (m: LensMode) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/* -------------------------------------------------------------------------- */
/*  Shared pill-row renderer                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Build a 3-pill segmented control bound to the global mode. Returns the
 * element so callers can attach it wherever they want, plus an
 * `unsubscribe()` to stop re-styling it when it's removed from the DOM.
 *
 * Used by `installGlobalLensControl` (top-level). The same primitive is
 * also exposed by `renderLensPills` for back-compat — all instances share
 * the same global state, so toggling one updates every other.
 */
function buildPillRow(opts: {
  compact?: boolean;
  onChange?: (m: LensMode) => void;
}): { el: HTMLElement; unsubscribe: () => void } {
  const compact = !!opts.compact;

  const row = document.createElement("div");
  row.setAttribute("role", "radiogroup");
  row.setAttribute("aria-label", "Decision lens");
  row.style.cssText = `
    display:inline-flex;gap:3px;padding:3px;
    background:rgba(33,33,33,0.75);
    backdrop-filter:blur(10px);
    border:1px solid rgba(255,255,255,0.08);
    border-radius:999px;
    font:${compact ? "600 10.5px" : "600 12px"} system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
    letter-spacing:0.02em;
  `;

  const modes: LensMode[] = ["none", "family", "bachelor", "investor"];
  const pills: { mode: LensMode; btn: HTMLButtonElement }[] = [];

  const paint = () => {
    for (const { mode, btn } of pills) {
      const active = mode === current;
      btn.setAttribute("aria-checked", String(active));
      btn.style.background = active ? "#FFD37E" : "transparent";
      btn.style.color = active ? "#1a1a1a" : "#f1f1f1";
      btn.style.fontWeight = active ? "700" : "500";
      btn.style.cursor = active ? "default" : "pointer";
      btn.style.boxShadow = active
        ? "0 1px 3px rgba(0,0,0,0.35)"
        : "none";
    }
  };

  for (const m of modes) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.setAttribute("role", "radio");
    btn.setAttribute("aria-label", `Decision lens: ${getModeLabel(m)}`);
    btn.textContent = getModeLabel(m);
    btn.style.cssText = `
      border:0;
      padding:${compact ? "4px 10px" : "6px 14px"};
      border-radius:999px;
      font:inherit;
      transition:background 140ms ease,color 140ms ease;
    `;
    btn.addEventListener("click", () => {
      if (m === current) return;
      setMode(m);
      opts.onChange?.(m);
    });
    btn.addEventListener("mouseenter", () => {
      if (m !== current) btn.style.background = "rgba(255,211,126,0.15)";
    });
    btn.addEventListener("mouseleave", () => {
      if (m !== current) btn.style.background = "transparent";
    });
    pills.push({ mode: m, btn });
    row.appendChild(btn);
  }

  paint();
  const unsubscribe = subscribe(() => paint());

  return { el: row, unsubscribe };
}

/* -------------------------------------------------------------------------- */
/*  Reset — one tap back to Exploration                                       */
/* -------------------------------------------------------------------------- */

/**
 * Clear every Decide-mode axis back to its neutral boot default, via the
 * public store setters (never DOM surgery) so every rendered control —
 * the Decide page pills, the status chip, the map emphasis — repaints
 * itself through its own subscription. The one shared reset: the Decide
 * page's Reset button and the status chip's ✕ both call this.
 */
export function resetToExploration(city: CityCode): void {
  try { setMode("none"); } catch { /* store unavailable — ignore */ }
  try { setPurchaseMode("buy"); } catch { /* ignore */ }
  try { setPropertyType("any"); } catch { /* ignore */ }
  try { setBudgetBucket("any", city); } catch { /* ignore */ }
  // The numeric range is a SEPARATE store from the bucket — a custom
  // sweep can leave userRange set while the bucket reads "any". Clearing
  // the bucket alone left the map silently filtered (hasEnoughSignal still
  // saw userRange) with the status chip hidden. Clear both.
  try { setUserBudgetRange(null); } catch { /* ignore */ }
  try { setPriority("none"); } catch { /* ignore */ }
  try { setFullMapView(false); } catch { /* ignore */ }
}


/**
 * Build a fresh 4-pill persona control (Any / Family / Bachelor / Investor)
 * for placement inside the mobile filter page or the desktop filter popup.
 *
 * Returns a { el, destroy } pair — call `destroy()` when removing the element
 * from the DOM so the subscribe() listener doesn't leak. Mobile filter open
 * builds a fresh pill row on every open and destroys it on close; that keeps
 * the control tidy and lets CSS (`[role=radiogroup]` selectors in the filter
 * page) style it as a stacked grid exactly like the Buy/Rent and House/
 * Apartment rows, so the whole page reads as ONE uniform set of pill steps.
 *
 * The control shares state with the top-right dropdown: flipping the pill
 * updates the global lens, and vice-versa.
 */
export function buildPersonaPillRow(): {
  el: HTMLElement;
  destroy: () => void;
} {
  const { el, unsubscribe } = buildPillRow({});
  return { el, destroy: unsubscribe };
}
